/**
 * ExportModal — put a run's list to work (plan §7.5): a three-step stepper —
 * (1) Destination: study + attach options (§6.6, D-5 fill-empty default),
 * (2) Campaign (collapsed by default): channel, template, start time, pace,
 *     skip-rule checklist with LIVE preflight counts from {dry_run:true},
 * (3) Confirm: the full arithmetic restated before the irreversible-ish step.
 * After launch it renders progress + receipts from GET /search-exports/:id and
 * offers Cancel while campaign rows remain scheduled.
 *
 * A campaign never sends from here: rows ride crm.scheduled_messages and the
 * worker re-runs every compliance gate at fire time (D-6).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Send, X } from 'lucide-react';
import api from '../../../services/api';
import type {
  ExportPreflight, ExportProgressInfo, ExportReceiptItem, MessageTemplate,
  SearchExport, SearchRunSummary, StudyOption, TeamMember,
} from '../../types';
import { dateTime, relTime } from '../../lib';
import { Alert, Z } from '../ui';

interface Props {
  run: SearchRunSummary;
  searchName: string | null;
  studies: StudyOption[];
  team: TeamMember[];
  onClose: () => void;
}

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const fieldCls = 'mt-1 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-300';
const checkCls = 'rounded border-gray-300 text-primary-600 focus:ring-primary-500';

const SKIP_LABELS: Record<string, string> = {
  opted_out: 'Opted out / consent revoked',
  no_channel: 'No phone/email on file',
  no_consent_basis: 'No consent basis (imported/manual, never texted us)',
  recently_contacted: 'Recently contacted',
  active_elsewhere: 'Active in another study',
};

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

/** Local datetime-input value for now+deltaMin, minute-floored. */
function localDatetimeValue(deltaMin: number): string {
  const d = new Date(Date.now() + deltaMin * 60000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ExportModal({ run, searchName, studies, team, onClose }: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [error, setError] = useState<string | null>(null);

  // Destination + attach (§6.6).
  const crmStudies = useMemo(
    () => studies.filter((s) => Boolean(s.id)).sort((a, b) => String(a.internal_title || a.title || a.slug).localeCompare(String(b.internal_title || b.title || b.slug))),
    [studies],
  );
  const [studyId, setStudyId] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);
  const [attachOn, setAttachOn] = useState(true);
  const [setConsidering, setSetConsidering] = useState<'fill_empty' | 'overwrite' | 'off'>('fill_empty');
  const [assignOwners, setAssignOwners] = useState<string>('off'); // off | round_robin | user:<id>
  const [addTag, setAddTag] = useState(true);
  const defaultTag = useMemo(
    () => `search:${searchName ? slugify(searchName) : `run-${run.id.slice(0, 8)}`}` || `search:run-${run.id.slice(0, 8)}`,
    [searchName, run.id],
  );
  const [tag, setTag] = useState(defaultTag);

  // Campaign (§6.7) — collapsed off by default.
  const [campaignOn, setCampaignOn] = useState(false);
  const [channel, setChannel] = useState<'sms' | 'email'>('sms');
  const [templateId, setTemplateId] = useState('');
  const [startAt, setStartAt] = useState(() => localDatetimeValue(60));
  const [pace, setPace] = useState(120);
  const [skipOptedOut, setSkipOptedOut] = useState(true);
  const [skipNoConsent, setSkipNoConsent] = useState(true); // O-1: default ON, per-campaign override
  const [skipRecent, setSkipRecent] = useState(true);
  const [recentDays, setRecentDays] = useState(7);
  const [skipActive, setSkipActive] = useState(true);

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  useEffect(() => {
    if (!campaignOn) return;
    api.get('/api/crm/templates', { params: { ...(studyId ? { study_id: studyId } : {}), channel } })
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, [campaignOn, studyId, channel]);
  const templatesByCategory = useMemo(() => {
    const groups: Record<string, MessageTemplate[]> = {};
    for (const t of templates) {
      (groups[t.category || 'general'] ||= []).push(t);
    }
    return groups;
  }, [templates]);

  // Live preflight (§7.5): one dry_run call recomputes everything, debounced.
  const [preflight, setPreflight] = useState<ExportPreflight | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState<string | null>(null);

  const buildParams = useCallback(() => ({
    study_id: studyId,
    only_new: onlyNew,
    attach: attachOn ? {
      set_considering: setConsidering,
      assign_owners: assignOwners,
      add_tag: addTag,
      ...(addTag && tag ? { tag } : {}),
    } : null,
    campaign: campaignOn ? {
      channel,
      ...(templateId ? { template_id: templateId } : {}),
      ...(startAt ? { start_at: new Date(startAt).toISOString() } : {}),
      pace_per_hour: pace,
      skip_rules: {
        opted_out: skipOptedOut,
        no_consent_basis: skipNoConsent,
        recently_contacted: skipRecent,
        recently_contacted_days: recentDays,
        active_elsewhere: skipActive,
      },
    } : null,
  }), [studyId, onlyNew, attachOn, setConsidering, assignOwners, addTag, tag, campaignOn,
    channel, templateId, startAt, pace, skipOptedOut, skipNoConsent, skipRecent, recentDays, skipActive]);

  const preflightSeq = useRef(0);
  useEffect(() => {
    if (!studyId || (!attachOn && !campaignOn)) { setPreflight(null); setPreflightError(null); return; }
    const seq = ++preflightSeq.current;
    const timer = setTimeout(async () => {
      setPreflightLoading(true);
      try {
        const res = await api.post(`/api/crm/search-runs/${run.id}/export`, { dry_run: true, params: buildParams() });
        if (seq !== preflightSeq.current) return;
        setPreflight(res.data?.preflight || null);
        setPreflightError(null);
      } catch (e: any) {
        if (seq !== preflightSeq.current) return;
        setPreflight(null);
        setPreflightError(errMsg(e));
      } finally {
        if (seq === preflightSeq.current) setPreflightLoading(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [run.id, studyId, attachOn, campaignOn, buildParams]);

  // Launch + progress.
  const [launching, setLaunching] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportRow, setExportRow] = useState<SearchExport | null>(null);
  const [progress, setProgress] = useState<ExportProgressInfo | null>(null);
  const [items, setItems] = useState<ExportReceiptItem[]>([]);
  const [canceling, setCanceling] = useState(false);

  const launch = async () => {
    setLaunching(true);
    setError(null);
    try {
      const res = await api.post(`/api/crm/search-runs/${run.id}/export`, { params: buildParams() });
      setExportId(res.data?.export_id || null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLaunching(false);
    }
  };

  const refreshExport = useCallback(async () => {
    if (!exportId) return null;
    try {
      const res = await api.get(`/api/crm/search-exports/${exportId}`, { params: { limit: 100 } });
      setExportRow(res.data?.export || null);
      setProgress(res.data?.progress || null);
      setItems(res.data?.items || []);
      return res.data;
    } catch (e: any) {
      setError(errMsg(e));
      return null;
    }
  }, [exportId]);

  useEffect(() => {
    if (!exportId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      const data = await refreshExport();
      if (!alive) return;
      const status = data?.export?.status;
      const remaining = data?.progress?.scheduled_remaining ?? 0;
      if (status === 'pending' || status === 'running') timer = setTimeout(tick, 1500);
      else if (remaining > 0) timer = setTimeout(tick, 5000); // sends still draining
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [exportId, refreshExport]);

  const cancelExport = async () => {
    if (!exportId) return;
    setCanceling(true);
    try {
      await api.patch(`/api/crm/search-exports/${exportId}/cancel`);
      await refreshExport();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setCanceling(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const campaignPf = preflight?.campaign;
  const attachPf = preflight?.attach;
  const templateBlocked = campaignOn && Boolean(campaignPf?.template_error);
  const capBlocked = campaignOn && Boolean(campaignPf?.cap_exceeded);
  const step1Ready = Boolean(studyId) && (attachOn || campaignOn);
  const confirmReady = step1Ready && !preflightLoading && Boolean(preflight)
    && !templateBlocked && !capBlocked && !preflightError;

  const skipRows: Array<{ key: string; on: boolean; toggle?: (v: boolean) => void; fixed?: boolean }> = [
    { key: 'opted_out', on: skipOptedOut, toggle: setSkipOptedOut },
    { key: 'no_channel', on: true, fixed: true },
    ...(channel === 'sms' ? [{ key: 'no_consent_basis', on: skipNoConsent, toggle: setSkipNoConsent }] : []),
    { key: 'recently_contacted', on: skipRecent, toggle: setSkipRecent },
    { key: 'active_elsewhere', on: skipActive, toggle: setSkipActive },
  ];

  const stepChip = (n: 1 | 2 | 3, title: string) => (
    <button
      type="button"
      onClick={() => { if (!exportId && (n === 1 || step1Ready)) setStep(n); }}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
        step === n
          ? 'bg-primary-600 text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
      }`}
    >
      <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${step === n ? 'bg-white/25' : 'bg-white dark:bg-gray-800'}`}>{n}</span>
      {title}
    </button>
  );

  return (
    <div className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/40 p-4`} onClick={onClose} role="presentation">
      <div
        className="flex max-h-[88vh] w-full max-w-xl flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Export list"
      >
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-3 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
              Export {run.final_total?.toLocaleString() ?? '—'} lead{run.final_total === 1 ? '' : 's'}
            </h2>
            {!exportId && (
              <div className="mt-1.5 flex items-center gap-1.5">
                {stepChip(1, 'Destination')}
                {stepChip(2, 'Campaign')}
                {stepChip(3, 'Confirm')}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <Alert tone="error" size="sm" icon={false} className="mb-3">{error}</Alert>
          )}

          {/* ---- Progress view (after launch) ---- */}
          {exportId ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                {exportRow?.status === 'complete' ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                  : exportRow?.status === 'failed' ? <AlertTriangle className="h-4 w-4 text-rose-500" />
                    : <Loader2 className="h-4 w-4 animate-spin text-primary-500" />}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {exportRow?.status === 'complete' ? 'Export complete'
                    : exportRow?.status === 'failed' ? 'Export failed'
                      : exportRow?.status === 'canceled' ? 'Export canceled'
                        : 'Exporting…'}
                </span>
                {exportRow?.updated_at && <span className="text-xs text-gray-400">updated {relTime(exportRow.updated_at)}</span>}
              </div>
              {exportRow?.error && (
                <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{exportRow.error}</p>
              )}

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  ['Attached', progress?.receipts?.attached],
                  ['Scheduled', progress?.receipts?.scheduled],
                  ['Skipped', progress?.receipts?.skipped],
                  ['Sent so far', progress?.sent_so_far],
                ].map(([label, n]) => (
                  <div key={label as string} className="rounded-lg border border-gray-200 px-2.5 py-1.5 dark:border-gray-700">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
                    <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">{(n as number | undefined)?.toLocaleString() ?? 0}</p>
                  </div>
                ))}
              </div>
              {(progress?.scheduled_remaining ?? 0) > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {progress!.scheduled_remaining.toLocaleString()} message{progress!.scheduled_remaining === 1 ? '' : 's'} still queued —
                  quiet hours and every compliance gate re-run at send time.
                </p>
              )}

              <ul className="max-h-64 divide-y divide-gray-50 overflow-y-auto rounded-lg border border-gray-100 dark:divide-gray-700/50 dark:border-gray-700">
                {items.map((it) => (
                  <li key={`${it.contact_id}:${it.outcome}`} className="flex items-center gap-2 px-2.5 py-1.5 text-xs">
                    <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-gray-100">
                      {it.contact?.full_name || `${it.contact?.first_name || ''} ${it.contact?.last_name || ''}`.trim() || it.contact_id.slice(0, 8)}
                    </span>
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      it.outcome === 'attached' || it.outcome === 'owner_assigned' ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : it.outcome === 'scheduled' ? (it.message?.status === 'sent' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300')
                          : it.outcome === 'failed' ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {it.outcome === 'scheduled' && it.message?.status === 'sent' ? 'sent'
                        : it.outcome === 'scheduled' && it.message?.status === 'canceled' ? 'send canceled'
                          : it.outcome}
                    </span>
                    {it.detail && <span className="hidden text-[10px] text-gray-400 sm:inline">{it.detail}</span>}
                    {it.outcome === 'scheduled' && it.message?.status === 'scheduled' && (
                      <span className="hidden text-[10px] text-gray-400 sm:inline">{dateTime(it.message.send_at)}</span>
                    )}
                  </li>
                ))}
                {items.length === 0 && <li className="px-3 py-4 text-center text-xs text-gray-400">No receipts yet…</li>}
              </ul>
            </div>
          ) : (
            <>
              {/* ---- Step 1: Destination ---- */}
              {step === 1 && (
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Destination study</label>
                    <select value={studyId} onChange={(e) => setStudyId(e.target.value)} aria-label="Destination study" className={fieldCls}>
                      <option value="">Pick a study…</option>
                      {crmStudies.map((s) => (
                        <option key={s.id!} value={s.id!}>{s.internal_title || s.title || s.slug}</option>
                      ))}
                    </select>
                  </div>
                  {(run.new_matches ?? 0) > 0 && (
                    <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
                      <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} className={checkCls} />
                      Only the {run.new_matches} new member{run.new_matches === 1 ? '' : 's'} since the last run
                    </label>
                  )}

                  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      <input type="checkbox" checked={attachOn} onChange={(e) => setAttachOn(e.target.checked)} className={checkCls} />
                      Attach leads to the study
                    </label>
                    {attachOn && (
                      <div className="mt-2 space-y-2.5 pl-6">
                        <div>
                          <label className={labelCls}>Considering study</label>
                          <select value={setConsidering} onChange={(e) => setSetConsidering(e.target.value as any)} aria-label="Considering mode" className={fieldCls}>
                            <option value="fill_empty">Set only where empty (recommended)</option>
                            <option value="overwrite">Overwrite — move everyone to this study</option>
                            <option value="off">Don't touch</option>
                          </select>
                          {setConsidering === 'overwrite' && (
                            <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                              Moves leads off their current considering study — use deliberately.
                            </p>
                          )}
                        </div>
                        <div>
                          <label className={labelCls}>Assign owners</label>
                          <select value={assignOwners} onChange={(e) => setAssignOwners(e.target.value)} aria-label="Owner assignment" className={fieldCls}>
                            <option value="off">Don't assign</option>
                            <option value="round_robin">Round-robin the study's recruiters (fills empty only)</option>
                            {team.length > 0 && (
                              <optgroup label="Assign all (fills empty only)">
                                {team.map((m) => <option key={m.id} value={`user:${m.id}`}>{m.name}</option>)}
                              </optgroup>
                            )}
                          </select>
                          {assignOwners === 'round_robin' && attachPf?.roster_size === 0 && (
                            <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">This study has no active recruiters — nobody will be assigned.</p>
                          )}
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
                            <input type="checkbox" checked={addTag} onChange={(e) => setAddTag(e.target.checked)} className={checkCls} />
                            Tag the list
                          </label>
                          {addTag && (
                            <input
                              value={tag}
                              onChange={(e) => setTag(e.target.value)}
                              aria-label="List tag"
                              className={`${fieldCls} font-mono text-xs`}
                              placeholder={defaultTag}
                            />
                          )}
                        </div>
                        {attachPf && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Will tag {attachPf.to_tag.toLocaleString()} · set considering on {attachPf.to_set_considering.toLocaleString()} · assign {attachPf.to_assign_owner.toLocaleString()} owner{attachPf.to_assign_owner === 1 ? '' : 's'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ---- Step 2: Campaign ---- */}
              {step === 2 && (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    <input type="checkbox" checked={campaignOn} onChange={(e) => setCampaignOn(e.target.checked)} className={checkCls} />
                    Send a message campaign to this list
                  </label>
                  {!campaignOn && (
                    <p className="pl-6 text-xs text-gray-500 dark:text-gray-400">
                      Optional — attach-only exports never message anyone. Campaigns schedule through the
                      normal send pipeline: quiet hours, opt-outs and consent are re-checked at send time.
                    </p>
                  )}
                  {campaignOn && (
                    <div className="space-y-3 pl-6">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className={labelCls}>Channel</label>
                          <select value={channel} onChange={(e) => { setChannel(e.target.value as any); setTemplateId(''); }} aria-label="Channel" className={fieldCls}>
                            <option value="sms">Text message (SMS)</option>
                            <option value="email">Email</option>
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Pace <span className="font-normal text-gray-400">(per hour, max 600)</span></label>
                          <input
                            type="number" min={1} max={600} value={pace}
                            onChange={(e) => setPace(Math.max(1, Math.min(600, parseInt(e.target.value, 10) || 120)))}
                            aria-label="Pace per hour" className={fieldCls}
                          />
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Template</label>
                        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} aria-label="Template" className={fieldCls}>
                          <option value="">Pick a template…</option>
                          {Object.entries(templatesByCategory).map(([cat, list]) => (
                            <optgroup key={cat} label={cat === 'study_details' ? 'Study details' : cat[0].toUpperCase() + cat.slice(1)}>
                              {list.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                            </optgroup>
                          ))}
                        </select>
                        {templateId && campaignPf?.template_error && (
                          <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">{campaignPf.template_error}</p>
                        )}
                        {!templateId && (
                          <p className="mt-1 text-[11px] text-gray-400">Merge fields render per lead at send time.</p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>Start</label>
                        <input
                          type="datetime-local" value={startAt} min={localDatetimeValue(0)} max={localDatetimeValue(14 * 24 * 60)}
                          onChange={(e) => setStartAt(e.target.value)} aria-label="Start time" className={fieldCls}
                        />
                        <p className="mt-1 text-[11px] text-gray-400">Up to 14 days out. Sends spread from here at the pace above.</p>
                      </div>

                      <div className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-200">Skip rules {preflightLoading && <Loader2 className="ml-1 inline h-3 w-3 animate-spin text-gray-400" />}</p>
                        <ul className="mt-1.5 space-y-1.5">
                          {skipRows.map(({ key, on, toggle, fixed }) => (
                            <li key={key} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox" checked={on} disabled={fixed}
                                onChange={(e) => toggle?.(e.target.checked)}
                                aria-label={SKIP_LABELS[key]}
                                className={`${checkCls} ${fixed ? 'opacity-60' : ''}`}
                              />
                              <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-100">
                                {SKIP_LABELS[key]}
                                {key === 'recently_contacted' && (
                                  <>
                                    {' '}within{' '}
                                    <input
                                      type="number" min={1} max={90} value={recentDays}
                                      onChange={(e) => setRecentDays(Math.max(1, Math.min(90, parseInt(e.target.value, 10) || 7)))}
                                      aria-label="Recently contacted days"
                                      className="w-12 rounded border border-gray-300 bg-white px-1 py-0.5 text-center text-xs dark:border-gray-600 dark:bg-gray-900"
                                    /> days
                                  </>
                                )}
                                {key === 'no_consent_basis' && (
                                  <span className="ml-1 text-[11px] text-gray-400">(uncheck for known-consented lists)</span>
                                )}
                                {fixed && <span className="ml-1 text-[11px] text-gray-400">(always on)</span>}
                              </span>
                              <span className="tabular-nums text-xs font-medium text-gray-500 dark:text-gray-400">
                                {on ? (campaignPf?.skips?.[key] ?? '—') : 'off'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {campaignPf && (
                        <p className={`text-xs ${campaignPf.cap_exceeded ? 'font-medium text-rose-600 dark:text-rose-400' : campaignPf.warn_large ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          Will schedule {campaignPf.to_schedule.toLocaleString()} {channel === 'sms' ? 'texts' : 'emails'}
                          {campaignPf.cap_exceeded
                            ? ` — over the ${campaignPf.cap.toLocaleString()} cap. Narrow the search and export twice.`
                            : campaignPf.warn_large ? ' — that’s a big blast; double-check the list.' : ''}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ---- Step 3: Confirm ---- */}
              {step === 3 && (
                <div className="space-y-3">
                  {preflightLoading || !preflight ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Computing the arithmetic…</div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900/40">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {[
                            attachOn && preflight.attach ? `Attach ${preflight.selected.toLocaleString()}` : null,
                            campaignOn && campaignPf ? `Schedule ${campaignPf.to_schedule.toLocaleString()} ${channel.toUpperCase()}` : null,
                            campaignOn && campaignPf ? `Skip ${(campaignPf.selected - campaignPf.to_schedule).toLocaleString()}` : null,
                          ].filter(Boolean).join(' · ')}
                          {' '}→ {preflight.study.title}
                        </p>
                        <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                          <li>{preflight.selected.toLocaleString()} member{preflight.selected === 1 ? '' : 's'} selected{onlyNew ? ' (new only)' : ''}{preflight.missing > 0 ? ` · ${preflight.missing} no longer active` : ''}</li>
                          {attachOn && attachPf && (
                            <li>
                              Attach: tag {attachPf.to_tag.toLocaleString()}{attachPf.tag ? ` (${attachPf.tag})` : ''} · considering {attachPf.to_set_considering.toLocaleString()} · owners {attachPf.to_assign_owner.toLocaleString()}
                            </li>
                          )}
                          {campaignOn && campaignPf && Object.entries(campaignPf.skips).filter(([, n]) => (n as number) > 0).map(([k, n]) => (
                            <li key={k}>Skip — {SKIP_LABELS[k]?.toLowerCase() || k}: {(n as number).toLocaleString()}</li>
                          ))}
                          {campaignOn && campaignPf && campaignPf.first_send_at && (
                            <li>First send {dateTime(campaignPf.first_send_at)}{campaignPf.last_send_at && campaignPf.to_schedule > 1 ? ` · last ${dateTime(campaignPf.last_send_at)}` : ''} · gates re-run at send time</li>
                          )}
                        </ul>
                      </div>
                      {attachOn && (attachPf?.warnings || []).map((w) => (
                        <p key={w} className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">{w}</p>
                      ))}
                      {campaignOn && campaignPf?.warn_large && !campaignPf.cap_exceeded && (
                        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                          Over 500 recipients — make sure this list is exactly who you mean.
                        </p>
                      )}
                      {capBlocked && (
                        <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                          Campaign exceeds the {campaignPf!.cap.toLocaleString()}-recipient cap — narrow the search first.
                        </p>
                      )}
                      {templateBlocked && (
                        <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                          Template: {campaignPf!.template_error}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
              {preflightError && step !== 2 && (
                <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{preflightError}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-5 py-3 dark:border-gray-700">
          {exportId ? (
            <>
              <div>
                {(progress?.scheduled_remaining ?? 0) > 0 && (
                  <button
                    type="button" onClick={cancelExport} disabled={canceling}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-700 dark:text-rose-400 dark:hover:bg-rose-900/20"
                  >
                    {canceling && <Loader2 className="h-4 w-4 animate-spin" />}
                    Cancel remaining sends
                  </button>
                )}
              </div>
              <button type="button" onClick={onClose} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
                Done
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => (step === 1 ? onClose() : setStep((step - 1) as 1 | 2))}
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {step === 1 ? 'Cancel' : 'Back'}
              </button>
              {step < 3 ? (
                <button
                  type="button"
                  onClick={() => setStep((step + 1) as 2 | 3)}
                  disabled={!step1Ready}
                  className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={launch}
                  disabled={!confirmReady || launching}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {campaignOn && campaignPf ? `Launch — ${campaignPf.to_schedule.toLocaleString()} sends` : 'Launch export'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
